import { 
  SplitterPaneProps
} from '@progress/kendo-react-layout/dist/npm/splitter/SplitterPane';
import React from 'react';
import { Splitter, SplitterOnChangeEvent } from '@progress/kendo-react-layout';
import DashboardDrawer from './DashboardDrawer';
import _ from 'lodash';
import DashboardGrid from './DashboardGrid';
import {
  DASHBOARD_PROPS,
  DASHBOARD_STATE,
  DASHBOARD_FILTER_PARAMS,
  PANES
} from './Types';
import { Component } from '../../dispatcher/modules';
import { Position } from '../../dispatcher/Types';
import SearchBar from '../search/SearchBar';
import HBox from '../layout/container/h_box/HBox';
import PuzzleButton from '../UI/button/PuzzleButton';
import ColumnsButton from '../grid/columns/ColumnsButton';
import classNames from 'classnames';
import Container from '../layout/container/Container';
import TabPanel from '../tab_panel/TabPanel';
import { TAB_PANEL_ITEM } from '../tab_panel/Types';
import Dispatcher from '../../dispatcher/Dispatcher';
import PuzzleDropDownButton from '../UI/button/dropdown/PuzzleDropDownButton';
import DashboardTitle from './components/DashboardTitle';
import { FILTER_AND_SORT_DATA } from '../grid/Types';
import { SEARCH_BAR_PROPS } from '../search/Types';
import VBox from '../layout/container/v_box/VBox';

/**
 * @class Dashboard
 * Dashboard class implements: header with search, grid and optionally drawer
 */
class Dashboard extends Component<DASHBOARD_PROPS, DASHBOARD_STATE> {
  private drawerBreakpoint = '';
  private tabPanelModuleId = `${this.props.moduleId}_tab_panel`;
  private searchBarModuleId = `${this.props.moduleId}_search_bar`;
  private dashboardGridModuleId = `${this.props.moduleId}_dashboard_grid`;
  private activeTabModuleId: string;

  constructor(props) {
    super(props);
    const drawerConfig = this.props.drawerConfig || {
      breakpoint: '40%',
      width: '30%',
      min: '30%',
      max: '60%'
    };
    this.drawerBreakpoint = drawerConfig?.breakpoint || '40%';
    const { width } = drawerConfig;
    const { min = '30%', max = '60%' } = drawerConfig;

    this.state.panes = [
      {}, {
        size: width ? width : '30%',
        max: max,
        min: min,
        collapsible: true,
        expanded: false,
        resizable: true,
        collapsed: true
      }
    ];
  }

  /**
   * Default state
   */
  state:DASHBOARD_STATE = {
    gridData: [],
    isDrawerVisible: false,
    selectedRowData: null,
    panes: [],
    columns: [],
    filterConfig: null,
    tabFiltersParams: {},
    isDisabledClearAllFilters: true
  }

  /**
   * @override
   */
  moduleDidMount() {
    const {
      customAction,
      customActionText = this.translateByModuleName('translation', 'customActionText', {}, 'Sidebar'),
      filterConfigUrl,
      gridConfig
    } = this.props;

    // updates the add action on sidebar, each route should have its own customAction to be set
    this.dispatch('UpdateSidebarCustomAction', Position.end, {
      customAction: {
        action: customAction,
        text: customActionText
      }
    });
    if (filterConfigUrl) {
      this.dispatch('getFilterData', '', {
        filterUrl: filterConfigUrl,
        navigationGridModuleId: gridConfig?.moduleId,
        navigationGridModuleName: gridConfig?.gridType === 'tree' ? 'NavigationTreeGrid' : 'NavigationGrid'
      });
    }
  }

  shouldComponentUpdate = (nextProps, nextState) => {
    if (_.get(this, 'props.drawerConfig.width') !== _.get(nextProps, 'drawerConfig.width')) {
      let panes = [...this.state.panes];
      panes[1] = {
        ...panes[1],
        size: _.get(nextProps, 'drawerConfig.width')
      };
      this.setState({
        panes: panes as PANES
      });
      return false;
    }

    return !(_.isEqual(this.state, nextState) && _.isEqual(this.props, nextProps));
  }

  /**
   * Sets panel states
   * @param {SplitterOnChangeEvent} event
   */
  onLayoutChange = (event: SplitterOnChangeEvent) => {
    this.setState({
      panes: event.newState as PANES
    });
  }

  /**
   * Checks if tabPanelConfig is set in the component's props.
   *
   * @returns {boolean} True if tabPanelConfig is set, otherwise false.
   */
  public hasTabPanelConfig = (): boolean => !!this.props.tabPanelConfig

  /**
   * Sets selected row data and triggers drawer if it is configured
   * @param {any} data
   */
  private onRowSelected = (data: any) => {
    let { panes } = this.state;
    this.setState({
      selectedRowData: data
    }, () => {
      _.get(this.props, 'gridConfig.onRowSelected') && this.props.gridConfig.onRowSelected(data, panes[1].collapsed);
    });
  }

  /**
   * On row deselect handler
   */
  onRowDeselected = () => {
    this.props.gridConfig.onRowDeselected?.();
  }

  /**
   * Sets selected row data
   * @param {any} rowData
   */
  setSelectedRowData = (rowData: any) => {
    this.setState({
      selectedRowData: rowData
    });
  }

  /**
   * Sets selected row data and triggers drawer on dbl click
   */
  onRowDoubleClick = () => {
    const { drawerConfig } = this.props;
    const { selectedRowData } = this.state;
    this.props?.gridConfig?.onRowDoubleClick?.(selectedRowData);

    const openDrawerOnRowDoubleClick = _.get(drawerConfig, 'openDrawerOnRowDoubleClick', true);
    if (drawerConfig && drawerConfig.drawer && openDrawerOnRowDoubleClick) {
      this.openDrawer();
    }
  }

  /**
   * Triggers actions on tab change.
   * It inspects if filterConfig is empty and sets the state based on that
   * It also inspects if searchBar value is empty and if it isn't - it triggers search in switched tab
   * @param {any} e
   */
  onTabSelect = (e:any) => {
    this.dispatch('setDashboardTitleTotalItems', '', { 
      totalElements: undefined, 
      totalSubElements: undefined 
    });
    this.onTabSelectSearchBarHandler(e.item);

    const columns = e?.item?.config?.columns;
    let filterConfig = e?.item?.config?.filterConfig;

    if (_.isEmpty(filterConfig)) {
      filterConfig = {
        fields: [],
        endPoints: {}
      };
    }

    if (columns) {
      this.setState({
        columns,
        filterConfig
      });
    }

    this.closeDrawer();

    this.props?.onSelectActiveTab?.(e?.item?.uniqueItemValue);
  };

  /**
   * Retrieves the active tab params
   * @param {string} [activeTabUniqueValue='main'] - The unique value identifying the active tab.
   * @returns {void}
   */
  public getActiveTabParams = (activeTabUniqueValue: string = 'main') => {
    const getActiveTab = this.state.tabFiltersParams[activeTabUniqueValue];

    const params = getActiveTab?.params;

    return {
      params
    };
  }

  /**
   * Enables the "Clear All Filters" button.
   * @returns {void}
   */
  public enableClearAllFiltersButton = (): void => {
    this.setState({
      isDisabledClearAllFilters: false
    });
  };

  /**
   * Function for saving filter params per dashboard grid tab
   * @param {string} activeTabUniqueValue
   * @param {DASHBOARD_FILTER_PARAMS} filterParams
   */
  public saveActiveTabFilterParams = (activeTabUniqueValue: string = 'main', filterParams: DASHBOARD_FILTER_PARAMS):void => {
    const { tabFiltersParams } = this.state;
    const getActiveTab = tabFiltersParams[activeTabUniqueValue];

    // If one of the previous actions was onFilterChange, then filterData should remain in filterParams
    if (_.isUndefined(filterParams?.filterData) && getActiveTab?.filterData) {
      filterParams.filterData = getActiveTab.filterData;
    }

    // Params obtained from quick search are deleted, except if the type is quickSearch or onSortChange
    if (filterParams.type !== 'quickSearch' && filterParams.type !== 'onSortChange' && filterParams?.params['search']) {
      delete filterParams.params['search'];
    }

    // If it is not type onFilterChange, then the params from previous filters should be saved
    if (getActiveTab?.params && filterParams?.type && filterParams.type !== 'onFilterChange') {
      // If the type is advancedFilter, then params from the previous onFilterChange filter take precedence
      filterParams.params = (filterParams.type === 'advancedFilter') ?
        {
          ...filterParams.params,
          ...getActiveTab.params
        } :
        {
          ...getActiveTab.params,
          ...filterParams.params
        };
      // If the type is onFilterChange then no params from previous filters should be saved except sort
    } else if (filterParams.type === 'onFilterChange' && getActiveTab?.params?.sort) {
      filterParams.params = {
        ...filterParams.params,
        sort: getActiveTab.params.sort
      };
    }

    // If one of the previous actions was onSortChange, then sort should remain in filterParams
    if (_.isUndefined(filterParams?.sort) && !_.isUndefined(getActiveTab?.sort)) {
      filterParams['sort'] = getActiveTab.sort;
    }

    const isActiveFilter = this.isFilterActive(filterParams?.params);

    this.setState({
      tabFiltersParams: {
        ...tabFiltersParams,
        [activeTabUniqueValue]: filterParams
      },
      isDisabledClearAllFilters: !isActiveFilter
    });
  }

  /**
   * Has a filter been applied to the tab
   * @param params
   */
  private isFilterActive = (params: {}):boolean => {
    let hasFilter = false;
    if (!_.isEmpty(params)) {
      for (const name in params) {
        if (!_.isNil(params[name])) {
          hasFilter = true;
          break;
        }
      }
    }

    return hasFilter;
  }

  /**
   * On active tab change, select latest filter for selected tab
   * @param {TAB_PANEL_ITEM} selectedTab
   */
  onTabSelectSearchBarHandler = (selectedTab: TAB_PANEL_ITEM) => {
    const { tabFiltersParams } = this.state;
    const selectedTabSearchValue = tabFiltersParams[selectedTab.uniqueItemValue];
    const { tabPanelConfig } = this.props;
    const keepTabsMounted = tabPanelConfig?.tabStripProps?.keepTabsMounted;
    this.activeTabModuleId = selectedTab?.config?.moduleId && keepTabsMounted ? selectedTab?.config?.moduleId : this.dashboardGridModuleId;
    let data = { 
      searchValue: '', 
      saveScroll: false,
      dashboardGridModuleId: this.activeTabModuleId,
      searchBarModuleId: this.searchBarModuleId,
      keepTabsMounted
    };

    if (selectedTabSearchValue) {
      data = { ...data, ...selectedTabSearchValue };
    }

    const isActiveFilter = this.isFilterActive(selectedTabSearchValue?.params);

    this.setState({ isDisabledClearAllFilters: !isActiveFilter }, () => {
      this.dispatch('updateFilterParamsBySelectedTab', {
        pos: Position.end,
        buffer: keepTabsMounted ? undefined : 200
      }, data);
    });
  }

  /**
   * On tab removed, clears filter params for removed tab
   * @param {TAB_PANEL_ITEM} uniqueItemValue
   */
  public onTabRemoved = ({ uniqueItemValue = 'main' }: TAB_PANEL_ITEM):void => {
    let tabFiltersParams = { ...this.state.tabFiltersParams };
    delete tabFiltersParams[uniqueItemValue];
    this.setState({ tabFiltersParams });
  }

  /**
   * Closes the drawer
   */
  closeDrawer = () => {
    if (this.props.drawerConfig) {
      if (this.props.tabPanelConfig) {
        this.props.tabPanelConfig?.items[0]?.config?.onDrawerToggle?.(false);
      } else {
        this.props?.gridConfig?.onDrawerToggle?.(false);
      }
    }

    this.setState({
      panes: this.getUpdatedPanes(true)
    });
  }

  /**
   * Opens the drawer
   */
  openDrawer = () => {
    if (this.props.drawerConfig) {
      if (this.props.tabPanelConfig) {
        this.props.tabPanelConfig?.items[0]?.config?.onDrawerToggle?.(true);
      } else {
        this.props?.gridConfig?.onDrawerToggle?.(true);
      }

      this.setState({
        panes: this.getUpdatedPanes(false)
      });
    }
  }

  /**
   * Flag witch indicates is drawer open
   */
  isDrawerOpen = (): boolean => !this.state.panes[1].collapsed;

  /**
   * Maps columns to corresponding filter fields
   * @param {any[]} filterConfig
   */
  static mapFilterFields = filterConfig => filterConfig.map(items => items.map(field => ({
    autoClose: field.autoClose,
    type: field.type,
    required: field.required,
    rules: field.rules,
    label: field.label,
    name: field.dataIndex,
    errorTxt: field.errorTxt,
    width: field.width,
    flexWidth: field.flexWidth,
    value: field.value,
    data: field.data,
    textField: field.textField,
    dataItemKey: field.dataItemKey,
    mappingValue: field.mappingValue,
    remoteVirtualScrolling: field.remoteVirtualScrolling
  })))

  /**
   * Clear all filters action
   */
  onClearAllFilters = () => {
    this.setState({ isDisabledClearAllFilters: true }, () => {
      const { tabPanelConfig } = this.props;
      const keepTabsMounted = tabPanelConfig?.tabStripProps?.keepTabsMounted;
      this.dispatch('ClearAllFilters', Position.end, {
        moduleId: this.moduleId,
        dashboardGridModuleId: keepTabsMounted ? this.activeTabModuleId : this.dashboardGridModuleId,
        searchBarModuleId: this.searchBarModuleId,
        tabPanelModuleId: this.tabPanelModuleId
      });
    });
  }
  /**
   * Handles click events on refresh button click
   */
  refreshBtnHandler = () => {
    const { actionName, actionType, data } = this.props.refreshBtnActionConfig;
    this.dispatch(actionName, actionType, data);
  }

  /**
   * Reconfigure columns
   * @param {any[]} columns
   */
  onReconfigure = ({ columns }) => {
    this.setState({ columns });
  }

  /**
   * Returns panes with updated collapsed state
   * @param {boolean} collapsed
   */
  getUpdatedPanes = (collapsed: boolean): PANES => [
    this.state.panes[0],
    {
      ...this.state.panes[1],
      collapsed
    }
  ]

  /**
   * Trigger when the sort and filter changes on the grid
   * @param {FILTER_AND_SORT_DATA} data
   */
  private onFilterAndSortChange = (data:FILTER_AND_SORT_DATA):void => {
    this.dispatch('saveActiveTabSearchBarValue', Position.end, { 
      ...data, 
      moduleId: this.moduleId,
      tabPanelModuleId: this.tabPanelModuleId,
      searchBarModuleId: this.searchBarModuleId
    });
  }

  render() {
    const drawerSize = this.state.panes[1].size;
    let {
      gridConfig,
      drawerConfig,
      tabPanelConfig,
      moduleId,
      title = '',
      subtitle,
      additionalBtns,
      searchConfig = {} as SEARCH_BAR_PROPS,
      className = '',
      hideClearAll = false,
      name,
      refreshBtnActionConfig,
      toolbarActions,
      inlineDashboardHeader = false,
      columnsButtonCustomRenderer,
      columnsButtonProps = {}
    } = this.props;
    if (!searchConfig.dashboard) {
      searchConfig.dashboard = name;
    }
    let { columns, filterConfig, isDisabledClearAllFilters } = this.state;
    const panes: SplitterPaneProps[] = this.state.panes.slice();

    const gridModuleId = `${moduleId}_grid`;
    if (gridConfig) {
      _.assign(gridConfig, {
        columns: gridConfig.columns,
        requestConfig: gridConfig.requestConfig,
        moduleId: gridModuleId
      });
    }

    const isDrawerOpen = this.isDrawerOpen();
    const dashboardCls = classNames(
      'tp-page tp-view-page ',
      {
        'tp-drawer-opened': isDrawerOpen
      }
    );

    const withAdditionalBtnsCls = additionalBtns ? 'with-additional-btns' : '';

    if (_.isEmpty(columns)) {
      if (gridConfig) {
        columns = gridConfig.columns;
      } else {
        columns = tabPanelConfig?.items[tabPanelConfig.selected]?.config?.columns;
        if (_.isEmpty(columns)) {
          columns = [];
        }
      }
    }

    if (_.isEmpty(filterConfig)) {
      if (gridConfig) {
        filterConfig = gridConfig.filterConfig;
      } else {
        filterConfig = tabPanelConfig?.items[tabPanelConfig.selected]?.config?.filterConfig;
      }
      if (_.isEmpty(filterConfig)) {
        filterConfig = {
          fields: [],
          endPoints: {}
        };
      }
    }

    if (this.props.showAdvancedFilters) {
      if (_.isEmpty(_.get(searchConfig, 'filterConfig.fields'))) {
        searchConfig = _.cloneDeep(this.props.searchConfig);
        searchConfig.filterConfig.fields = Dashboard.mapFilterFields(filterConfig.fields);
      }
      if (_.isEmpty(_.get(searchConfig, 'filterConfig.endPoints'))) {
        searchConfig.filterConfig.endPoints = filterConfig.endPoints;
      }
    }

    const isDrawerBreakpoint = parseInt(drawerSize.replace('%', '')) > parseInt(this.drawerBreakpoint.replace('%', ''));

    let toolbarActionItems;
    if (toolbarActions) {
      const { actions, actionsBeforeDropDown, dropDownActionsNames } = toolbarActions;
      const visibleActions = actions.filter(item => !item.hidden);
      const dropDownActions = visibleActions.slice(actionsBeforeDropDown);

      if (visibleActions.length) {
        toolbarActionItems = visibleActions.slice(0, actionsBeforeDropDown).map(actionItem => (
          <PuzzleButton
            data-cy={actionItem.dataCy}
            key={actionItem.key}
            fillMode={actionItem.fillMode ?? 'solid'}
            iconClass={actionItem.iconClass}
            themeColor={actionItem.themeColor || 'primary'}
            disabled={actionItem.disabled}
            hidden={actionItem.hidden}
            onClick={() => {
              Dispatcher.dispatch(actionItem.action.actionName, actionItem.action.actionType || Position.end, actionItem.action.data);
            }}
          >{actionItem.text}</PuzzleButton>
        ));
        if (visibleActions.length > actionsBeforeDropDown && !dropDownActionsNames) {
          toolbarActionItems.push(
            <PuzzleDropDownButton
              iconClass="tp-icon tp-more-vertical k-rotate-90"
              items={dropDownActions}
              key={'dashboard-toolbar-action-dropdown-button'}
              fillMode={'solid'}
              themeColor={'primary'}
              onItemClick={(event) => {
                const actionItem = event.item;
                Dispatcher.dispatch(actionItem.action.actionName, actionItem.action.actionType || Position.end, actionItem.action.data);
              }}
              textField="text"
            />
          );
        }
        else if (dropDownActionsNames) {
          let actionsInfo = {};
          dropDownActions.forEach((item: any) => {
            const { dropdownActionsName } = item;
            if (_.isUndefined(actionsInfo[dropdownActionsName])) {
              actionsInfo[dropdownActionsName] = [item];
            } else {
              actionsInfo[dropdownActionsName].push(item);
            }
          });

          dropDownActionsNames.forEach((item:any) => {
            const { name, text, reverse = true, themeColor = 'primary', fillMode = 'solid', iconClass = 'k-button-icon tp-icon tp-arrow-chevron-down' } = item;
            const items = actionsInfo[name];

            if (!_.isUndefined(items)) {
              toolbarActionItems.push(
                <PuzzleDropDownButton
                  iconClass={iconClass}
                  items={items}
                  key={`dashboard-toolbar-action-dropdown-button-${text}`}
                  fillMode={fillMode}
                  themeColor={themeColor}
                  onItemClick={(event) => {
                    const actionItem = event.item;
                    Dispatcher.dispatch(actionItem.action.actionName, actionItem.action.actionType || Position.end, actionItem.action.data);
                  }}
                  reverse={reverse}
                  text={text}
                />
              );
            }
          });
        }
      }
    }

    const dashBoardTitleElement =
      (
        <DashboardTitle
          title={title}
          subtitle={subtitle}
          className={'tp-page-title'}
        />);

    const searchBarElement =
      (
        <Container
          displayFlex
          alignItemsCenter
          justifyContentCenter={!!additionalBtns}
          className={inlineDashboardHeader ? 'tp-page-searchbar-inline' : 'tp-page-searchbar tp-gap-2'}

        >
          <SearchBar
            moduleId={this.searchBarModuleId}
            {
              ...searchConfig
            }
            filterConfig={{
              ...searchConfig.filterConfig
            }}
          />
          <Container></Container>
        </Container>
      );

    additionalBtns = React.Children.map(additionalBtns, (btn, index) =>
      React.cloneElement(btn, {
        dataCy: `additionalBtns-${index}`,
        key: `dashboard-toolbar-additional-btn-${index}`
      })
    );

    const additionalButtonsElement = (
      <HBox
        alignItemsCenter
        justifyContentEnd
        className={'tp-view-action-toolbar'}
        dataCy={'additional-action-container'}
      >
        { additionalBtns }
      </HBox>
    );

    const toolbarButtonsElement = (
      <HBox
        className={'tp-main-grid-topbar'}
        gap={2}
      >
        {
          hideClearAll ? '' : <PuzzleButton
            data-cy={'clearAllFilters'}
            onClick={this.onClearAllFilters}
            iconClass={'tp-icon tp-clear-filters'}
            titleText={this.translate('clearAllFilters')}
            fillMode={'outline'}
            themeColor={'primary'}
            disabled={isDisabledClearAllFilters}
          />
        }
        <PuzzleButton
          data-cy={'refresh'}
          iconClass={'tp-icon tp-refresh'}
          hidden={!refreshBtnActionConfig}
          onClick={this.refreshBtnHandler}
          titleText={this.translate('refreshBtn')}
          fillMode={'outline'}
          themeColor={'primary'}
        />
        {columnsButtonCustomRenderer ? columnsButtonCustomRenderer() : (
          <ColumnsButton
            columns={columns}
            withText={false}
            gridModuleId={gridModuleId}
            {...columnsButtonProps}
          />
        )}
      </HBox>
    );

    const dashboardStandardHeaderTop = (
      <HBox
        alignItemsCenter
        wrap
        justifyContentBetween={!!additionalBtns}
        className={`tp-page-section tp-page-header k-widget tp-no-border tp-gap-2 data-allowrecorddeselect ${withAdditionalBtnsCls}`}
        htmlTag={'section'}
      >
        { dashBoardTitleElement }
        { searchBarElement }
        {
          additionalBtns ? additionalButtonsElement : ''
        }
      </HBox>
    );

    return (
      <div
        className={`dashboard ${dashboardCls} ${className}`}
      >
        <Splitter
          className={'tp-no-border data-preventrecorddeselect'}
          panes={panes}
          onChange={this.onLayoutChange}
          orientation={'horizontal'}
        >
          <VBox className={'tp-page-wrapper'}>
            { !inlineDashboardHeader ? dashboardStandardHeaderTop : '' }
            <HBox
              justifyContentBetween={!!toolbarActions}
              justifyContentEnd={!toolbarActions}
              justifyContentStart={inlineDashboardHeader}
              alignItemsBaseLine={true}
              htmlTag={'section'}
              className={inlineDashboardHeader ? 'tp-page-header tp-inline-header tp-page-section' : 'tp-page-header tp-page-section'}
              gap={2}
            >
              { inlineDashboardHeader ? dashBoardTitleElement : ''}
              <HBox
                gap={2}
                className={inlineDashboardHeader ? 'tp-inline-header-right-section' : 'tp-header-bottom-section'}
                justifyContentBetween

              >
                { inlineDashboardHeader ? searchBarElement : ''}
                <HBox
                  className={'tp-header-right-section-buttons'}
                  justifyContentBetween={!!toolbarActionItems}
                  justifyContentEnd={!toolbarActionItems}
                  gap={2}
                >
                  {
                    additionalBtns && inlineDashboardHeader ? additionalButtonsElement : ''
                  }
                  {
                    toolbarActions ?
                      <HBox
                        className={'tp-main-grid-topbar'}
                        dataCy={'main-grid-topbar'}
                        data-preventrecorddeselect={'true'}
                        gap={2}
                      >
                        {toolbarActionItems}
                      </HBox> :
                      <></>
                  }
                  { toolbarButtonsElement }
                </HBox>
              </HBox>
            </HBox>

            <section
              className={'tp-page-section tp-view-grid k-widget tp-no-border data-allowrecorddeselect tp-gap-2'}
              data-cy={'dashboard-page-section'}
            >
              {
                tabPanelConfig ? <TabPanel
                  {...tabPanelConfig}
                  onFilterAndSortChange={this.onFilterAndSortChange}
                  onReconfigure={this.onReconfigure}
                  onSelect={this.onTabSelect}
                  onTabRemoved={this.onTabRemoved}
                  moduleId={this.tabPanelModuleId}
                  dashboardGridModuleId={this.dashboardGridModuleId}
                  onRowSelected={this.setSelectedRowData}
                  shouldUseSearchBar={true}
                  getActiveTabParams={this.getActiveTabParams.bind(this)}
                /> : (gridConfig.grid && React.cloneElement(gridConfig.grid, {
                  ...gridConfig,
                  columns,
                  onFilterAndSortChange: this.onFilterAndSortChange,
                  onReconfigure: this.onReconfigure,
                  onRowSelected: this.onRowSelected,
                  onRowDoubleClick: this.onRowDoubleClick
                })) || <DashboardGrid
                  {...gridConfig}
                  columns={columns}
                  onFilterAndSortChange={this.onFilterAndSortChange}
                  onReconfigure={this.onReconfigure}
                  onRowSelected={this.onRowSelected}
                  onRowDoubleClick={this.onRowDoubleClick}
                  onRowDeselected={this.onRowDeselected}
                  moduleId={this.dashboardGridModuleId}
                  shouldUseSearchBar={true}
                />
              }
            </section>
          </VBox>
          {
            _.get(this, 'props.drawerConfig.drawer') ?
              <DashboardDrawer
                className={isDrawerBreakpoint ? '' : 'tp-drawer-resized'}
                drawerData={this.state.selectedRowData}
                onDrawerToggle={tabPanelConfig ? tabPanelConfig?.items[0]?.config?.onDrawerToggle : gridConfig?.onDrawerToggle}
                {...drawerConfig}
              /> : <></>
          }
        </Splitter>
      </div>
    );
  }
}

export default Dashboard;
